import { useState } from 'react';
import { StanDivider, StanDropdown, StanTipTapEditor, StanTipTapEditorCompact } from '@/components';
import { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

export default function InternalsTipTapEditorPage() {
    // Basic editor states
    const [basicEditorValue, setBasicEditorValue] = useState(
        '<p>This is a basic TipTap editor with default settings.</p>'
    );
    const [richEditorValue, setRichEditorValue] = useState(
        '<h3>Rich Text Editor</h3><p>This editor supports <strong>bold</strong>, <em>italic</em>, and other formatting options.</p>'
    );
    const [aiEditorValue, setAiEditorValue] = useState(
        '<p>Type something and use the AI generate feature to enhance your content.</p>'
    );
    const [personalizedEditorValue, setPersonalizedEditorValue] = useState(
        '<p>Hello {{my_name}}, welcome to your personalized content editor!</p>'
    );
    const [limitedEditorValue, setLimitedEditorValue] = useState(
        '<p>This editor has limited features - no images, videos, or AI generation.</p>'
    );
    const [disabledEditorValue, setDisabledEditorValue] = useState('<p>This editor is in a disabled state.</p>');
    const [errorEditorValue, setErrorEditorValue] = useState('<p>This editor is in an error state.</p>');

    // Compact editor states
    const [compactBasicValue, setCompactBasicValue] = useState('This is a compact single-line editor');
    const [compactPersonalizedValue, setCompactPersonalizedValue] = useState(
        'Hello {{customer_name}}, welcome to {{company_name}}!'
    );
    const [compactDisabledValue, setCompactDisabledValue] = useState('This compact editor is disabled');
    const [compactErrorValue, setCompactErrorValue] = useState('This compact editor has an error');

    // Playground editor states
    const [playgroundValue, setPlaygroundValue] = useState(
        '<h3>Playground Editor</h3><p>Customize the settings below to see how they affect the editor behavior.</p>'
    );
    const [imageUpload, setImageUpload] = useState<DropdownValue>({ label: 'Enabled', value: 'true' });
    const [linkAllowed, setLinkAllowed] = useState<DropdownValue>({ label: 'Enabled', value: 'true' });
    const [videoAllowed, setVideoAllowed] = useState<DropdownValue>({ label: 'Enabled', value: 'true' });
    const [aiGeneration, setAiGeneration] = useState<DropdownValue>({ label: 'Enabled', value: 'true' });
    const [productLinks, setProductLinks] = useState<DropdownValue>({ label: 'Enabled', value: 'true' });
    const [editorDisabled, setEditorDisabled] = useState<DropdownValue>({ label: 'False', value: 'false' });
    const [editorError, setEditorError] = useState<DropdownValue>({ label: 'False', value: 'false' });

    // Mock personalization data
    const personalizeItemData = [
        { name: 'Customer Name', id: 'customer_name', required: true },
        { name: 'My Name', id: 'my_name', required: false },
        { name: 'Company Name', id: 'company_name', required: false },
        { name: 'Product Name', id: 'product_name', required: false },
        { name: 'Order Number', id: 'order_number', required: false },
    ];

    // AI Generation mock function
    const generateAiSuggestion = async (prompt: string) => {
        // Simulate AI generation delay
        return new Promise<string>(resolve => {
            setTimeout(() => {
                const suggestions = [
                    `<p><strong>AI Enhanced Content:</strong> Based on your prompt "${prompt}", here's an enhanced version with better structure and engaging language.</p><p>This content has been optimized for clarity and impact, incorporating best practices for user engagement.</p>`,
                    `<h3>AI-Generated Expansion</h3><p>Your prompt: "${prompt}"</p><p>Here's a comprehensive expansion that includes relevant details, engaging storytelling elements, and a clear call-to-action to maximize effectiveness.</p>`,
                    `<p><em>AI Suggestion:</em> "${prompt}" can be enhanced with the following content:</p><ul><li>Clear and concise messaging</li><li>Engaging visual elements</li><li>Strong call-to-action</li></ul><p>This approach will help improve user engagement and conversion rates.</p>`,
                ];
                const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
                resolve(randomSuggestion || '');
            }, 2000);
        });
    };

    const booleanOptions = [
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
    ];

    const enabledOptions = [
        { label: 'Enabled', value: 'true' },
        { label: 'Disabled', value: 'false' },
    ];

    return (
        <div className='space-y-8'>
            {/* TipTap Editor Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <div className='mb-8'>
                    <h2 className='text-2xl font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                        TipTap Rich Text Editor
                    </h2>
                    <p className='mt-2' style={{ color: 'var(--text-color-gray)' }}>
                        Comprehensive showcase of TipTap editor configurations and features
                    </p>
                </div>

                <div className='space-y-12'>
                    {/* Basic Editors Grid */}
                    <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                        {/* 1. Basic Editor */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Basic Editor
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Standard configuration with all features enabled
                                </p>
                            </div>

                            <StanTipTapEditor
                                value={basicEditorValue}
                                onChange={setBasicEditorValue}
                                onAiGenerate={generateAiSuggestion}
                            />
                        </div>

                        {/* 2. Rich Text Editor */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Rich Text Editor
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Pre-populated with formatted content
                                </p>
                            </div>

                            <StanTipTapEditor
                                value={richEditorValue}
                                onChange={setRichEditorValue}
                                onAiGenerate={generateAiSuggestion}
                            />
                        </div>

                        {/* 3. AI-Enhanced Editor */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    AI-Enhanced Editor
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Try the AI generation feature with custom prompts
                                </p>
                            </div>

                            <StanTipTapEditor
                                value={aiEditorValue}
                                onChange={setAiEditorValue}
                                onAiGenerate={generateAiSuggestion}
                                generateAiAllowed
                            />
                            <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-info-subtle)' }}>
                                <p className='text-sm' style={{ color: 'var(--text-color-info-bold)' }}>
                                    💡 Click the AI button in the toolbar and enter a prompt to see AI content
                                    generation in action
                                </p>
                            </div>
                        </div>

                        {/* 4. Personalized Content Editor */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Personalized Content
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Editor with personalization variables
                                </p>
                            </div>

                            <StanTipTapEditor
                                value={personalizedEditorValue}
                                onChange={setPersonalizedEditorValue}
                                personalizeItemData={personalizeItemData}
                                onAiGenerate={generateAiSuggestion}
                            />
                            <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-success-subtle)' }}>
                                <p className='text-sm' style={{ color: 'var(--text-color-success-bold)' }}>
                                    ✨ Use the personalization dropdown to insert dynamic variables like{' '}
                                    {personalizeItemData.find(item => item.id === 'customer_name')?.name}
                                </p>
                            </div>
                        </div>

                        {/* 5. Limited Features Editor */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Limited Features
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Text-only editor with restricted functionality
                                </p>
                            </div>

                            <StanTipTapEditor
                                value={limitedEditorValue}
                                onChange={setLimitedEditorValue}
                                imageUploadAllowed={false}
                                videoAllowed={false}
                                generateAiAllowed={false}
                                allowAddProductLink={false}
                            />
                            <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-warning-subtle)' }}>
                                <p className='text-sm' style={{ color: 'var(--text-color-warning-bold)' }}>
                                    ⚠️ This editor has image upload, video, AI generation, and product links disabled
                                </p>
                            </div>
                        </div>

                        {/* 6. Disabled State */}
                        <div className='space-y-4'>
                            <div>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Disabled State
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Read-only editor in disabled state
                                </p>
                            </div>

                            <StanTipTapEditor value={disabledEditorValue} onChange={setDisabledEditorValue} disabled />
                        </div>
                    </div>

                    <StanDivider padded label='Interactive Playground' />

                    {/* Interactive Playground */}
                    <div className='space-y-6'>
                        <div>
                            <h3 className='text-xl font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                                Interactive Editor Playground
                            </h3>
                            <p className='mt-2' style={{ color: 'var(--text-color-gray)' }}>
                                Customize the editor settings below to see how they affect functionality
                            </p>
                        </div>

                        {/* Configuration Controls */}
                        <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4'>
                            <StanDropdown
                                id='image-upload-dropdown'
                                name='image-upload'
                                label='Image Upload'
                                options={enabledOptions}
                                onSelect={setImageUpload}
                                currentValue={imageUpload}
                            />
                            <StanDropdown
                                id='link-allowed-dropdown'
                                name='link-allowed'
                                label='Links'
                                options={enabledOptions}
                                onSelect={setLinkAllowed}
                                currentValue={linkAllowed}
                            />
                            <StanDropdown
                                id='video-allowed-dropdown'
                                name='video-allowed'
                                label='Video Upload'
                                options={enabledOptions}
                                onSelect={setVideoAllowed}
                                currentValue={videoAllowed}
                            />
                            <StanDropdown
                                id='ai-generation-dropdown'
                                name='ai-generation'
                                label='AI Generation'
                                options={enabledOptions}
                                onSelect={setAiGeneration}
                                currentValue={aiGeneration}
                            />
                            <StanDropdown
                                id='product-links-dropdown'
                                name='product-links'
                                label='Product Links'
                                options={enabledOptions}
                                onSelect={setProductLinks}
                                currentValue={productLinks}
                            />
                            <StanDropdown
                                id='editor-disabled-dropdown'
                                name='editor-disabled'
                                label='Disabled'
                                options={booleanOptions}
                                onSelect={setEditorDisabled}
                                currentValue={editorDisabled}
                            />
                            <StanDropdown
                                id='editor-error-dropdown'
                                name='editor-error'
                                label='Error State'
                                options={booleanOptions}
                                onSelect={setEditorError}
                                currentValue={editorError}
                            />
                        </div>

                        {/* Playground Editor */}
                        <div
                            className='rounded-lg p-6'
                            style={{
                                border: '2px solid var(--color-border-subtle)',
                                backgroundColor: 'var(--color-bg-canvas)',
                            }}
                        >
                            <StanTipTapEditor
                                value={playgroundValue}
                                onChange={setPlaygroundValue}
                                imageUploadAllowed={imageUpload.value === 'true'}
                                linkAllowed={linkAllowed.value === 'true'}
                                videoAllowed={videoAllowed.value === 'true'}
                                generateAiAllowed={aiGeneration.value === 'true'}
                                allowAddProductLink={productLinks.value === 'true'}
                                disabled={editorDisabled.value === 'true'}
                                error={editorError.value === 'true'}
                                personalizeItemData={personalizeItemData}
                                onAiGenerate={generateAiSuggestion}
                            />
                        </div>

                        {/* Configuration Summary */}
                        <div
                            className='rounded-lg p-4'
                            style={{
                                border: '1px solid var(--color-border-subtle)',
                                backgroundColor: 'var(--color-bg-subtle)',
                            }}
                        >
                            <h4 className='mb-3 font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                Current Configuration
                            </h4>
                            <div className='grid grid-cols-2 gap-2 text-sm md:grid-cols-4'>
                                <div>
                                    <span style={{ color: 'var(--text-color-gray)' }}>Images:</span>{' '}
                                    <span
                                        style={{
                                            color:
                                                imageUpload.value === 'true'
                                                    ? 'var(--text-color-success-bold)'
                                                    : 'var(--text-color-negative-bold)',
                                        }}
                                    >
                                        {imageUpload.label}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: 'var(--text-color-gray)' }}>Links:</span>{' '}
                                    <span
                                        style={{
                                            color:
                                                linkAllowed.value === 'true'
                                                    ? 'var(--text-color-success-bold)'
                                                    : 'var(--text-color-negative-bold)',
                                        }}
                                    >
                                        {linkAllowed.label}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: 'var(--text-color-gray)' }}>Videos:</span>{' '}
                                    <span
                                        style={{
                                            color:
                                                videoAllowed.value === 'true'
                                                    ? 'var(--text-color-success-bold)'
                                                    : 'var(--text-color-negative-bold)',
                                        }}
                                    >
                                        {videoAllowed.label}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: 'var(--text-color-gray)' }}>AI:</span>{' '}
                                    <span
                                        style={{
                                            color:
                                                aiGeneration.value === 'true'
                                                    ? 'var(--text-color-success-bold)'
                                                    : 'var(--text-color-negative-bold)',
                                        }}
                                    >
                                        {aiGeneration.label}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <StanDivider padded label='Error State Example' />

                    {/* Error State Example */}
                    <div className='space-y-6'>
                        <div>
                            <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                Error State
                            </h4>
                            <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                Editor with error styling applied
                            </p>
                        </div>
                        <div
                            className='rounded-lg p-4'
                            style={{
                                border: '1px solid var(--color-border-subtle)',
                                backgroundColor: 'var(--color-bg-subtle)',
                            }}
                        >
                            <StanTipTapEditor
                                value={errorEditorValue}
                                onChange={setErrorEditorValue}
                                error
                                onAiGenerate={generateAiSuggestion}
                            />
                        </div>
                        <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-negative-subtle)' }}>
                            <p className='text-sm' style={{ color: 'var(--text-color-negative-bold)' }}>
                                ❌ This editor is in an error state - notice the red border styling
                            </p>
                        </div>
                    </div>

                    <StanDivider label='Compact Editor (Single-Line)' />

                    {/* Compact Editor Section */}
                    <div className='space-y-6'>
                        <div>
                            <h3 className='text-xl font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                                Compact TipTap Editor
                            </h3>
                            <p className='mt-2' style={{ color: 'var(--text-color-gray)' }}>
                                Optimized for single-line inputs like email subjects. Features: no Enter key, paste
                                sanitization, external personalize button, simplified bubble menu.
                            </p>
                        </div>

                        <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                            {/* Basic Compact */}
                            <div className='space-y-4'>
                                <div>
                                    <h4
                                        className='text-lg font-medium'
                                        style={{ color: 'var(--text-color-secondary)' }}
                                    >
                                        Basic Compact Editor
                                    </h4>
                                    <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                        Single-line editor without personalization
                                    </p>
                                </div>
                                <StanTipTapEditorCompact
                                    value={compactBasicValue}
                                    onChange={setCompactBasicValue}
                                    placeholder='Type a single line of text...'
                                />
                                <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-info-subtle)' }}>
                                    <p className='text-sm' style={{ color: 'var(--text-color-info-bold)' }}>
                                        💡 Try pressing Enter - it won&apos;t create a new line. Paste multi-line text
                                        to see it automatically converted to single-line.
                                    </p>
                                </div>
                            </div>

                            {/* Compact with Personalization */}
                            <div className='space-y-4'>
                                <div>
                                    <h4
                                        className='text-lg font-medium'
                                        style={{ color: 'var(--text-color-secondary)' }}
                                    >
                                        Compact with Personalization
                                    </h4>
                                    <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                        Single-line editor with personalization variables
                                    </p>
                                </div>
                                <StanTipTapEditorCompact
                                    value={compactPersonalizedValue}
                                    onChange={setCompactPersonalizedValue}
                                    personalizeItemData={personalizeItemData}
                                    placeholder='Add personalized content...'
                                />
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-success-subtle)' }}
                                >
                                    <p className='text-sm' style={{ color: 'var(--text-color-success-bold)' }}>
                                        ✨ Click the &quot;Personalize&quot; button below the editor to insert variables
                                    </p>
                                </div>
                            </div>

                            {/* Compact Disabled */}
                            <div className='space-y-4'>
                                <div>
                                    <h4
                                        className='text-lg font-medium'
                                        style={{ color: 'var(--text-color-secondary)' }}
                                    >
                                        Disabled Compact Editor
                                    </h4>
                                    <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                        Read-only compact editor
                                    </p>
                                </div>
                                <StanTipTapEditorCompact
                                    value={compactDisabledValue}
                                    onChange={setCompactDisabledValue}
                                    disabled
                                />
                            </div>

                            {/* Compact Error */}
                            <div className='space-y-4'>
                                <div>
                                    <h4
                                        className='text-lg font-medium'
                                        style={{ color: 'var(--text-color-secondary)' }}
                                    >
                                        Error State Compact Editor
                                    </h4>
                                    <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                        Compact editor with error styling
                                    </p>
                                </div>
                                <StanTipTapEditorCompact
                                    value={compactErrorValue}
                                    onChange={setCompactErrorValue}
                                    error
                                />
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-negative-subtle)' }}
                                >
                                    <p className='text-sm' style={{ color: 'var(--text-color-negative-bold)' }}>
                                        ❌ Notice the red border and error styling
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Comparison */}
                        <div
                            className='rounded-lg p-4'
                            style={{
                                border: '1px solid var(--color-border-subtle)',
                                backgroundColor: 'var(--color-bg-subtle)',
                            }}
                        >
                            <h4 className='mb-3 font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                Compact vs Regular Editor
                            </h4>
                            <div className='space-y-2 text-sm'>
                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <strong style={{ color: 'var(--text-color-dark)' }}>Compact Editor:</strong>
                                        <ul className='mt-1 ml-4 list-disc' style={{ color: 'var(--text-color-gray)' }}>
                                            <li>Single-line only (Enter disabled)</li>
                                            <li>No main toolbar</li>
                                            <li>External personalize button</li>
                                            <li>Simplified bubble menu</li>
                                            <li>Paste sanitization</li>
                                            <li>Perfect for subjects/titles</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <strong style={{ color: 'var(--text-color-dark)' }}>Regular Editor:</strong>
                                        <ul className='mt-1 ml-4 list-disc' style={{ color: 'var(--text-color-gray)' }}>
                                            <li>Multi-line support</li>
                                            <li>Full toolbar with formatting</li>
                                            <li>Image/video upload</li>
                                            <li>AI generation</li>
                                            <li>Product links</li>
                                            <li>Perfect for body content</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
